import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { periverseTheme } from '@peritasai/ui-shared';
import cartIllustration from '../../assets/images/cart-Illustration.svg';
import type { DiscoveryHeader } from './header';
import { AssetType } from '@peritasai/types';
import type { DetectedAsset } from './types';
import {
  discoveryActionsSx,
  discoveryAssignButtonSx,
  discoveryCancelButtonSx,
  discoveryCardSx,
  discoveryCardWrapSx,
  discoveryCartIllustrationSx,
  discoveryCheckboxSx,
  discoveryContainerSx,
  discoveryDetectedListSx,
  discoveryDetectedTitleSx,
  discoveryDetectedTrayBadgeSx,
  discoveryDetectedTrayBadgeTextSx,
  discoveryDetectedTrayNameSx,
  discoveryDetectedTrayRowSx,
  discoveryDetectedWrapSx,
  discoveryHeaderSx,
  discoveryHeaderTitleStackSx,
  discoveryHeaderWrapSx,
  discoveryPanelSx,
  discoverySearchingLabelSx,
  discoveryStatusLabelSx,
  discoveryStatusRowSx,
  discoveryStatusTitleSx,
  discoverySubtitleSx,
  discoverySuccessIconWrapSx,
  discoverySuccessOuterSx,
  discoverySuccessPanelSx,
  discoverySuccessTitleSx,
  discoveryTitleSx,
  discoveryWaitingCopySx,
} from './styles';

export type DiscoveryCardCopy = {
  waitingTitle: string;
  searchingLabel: string;
  detectedTitle: string;
  successTitle: string;
  assignLabel: string;
};

const SearchingIndicator = ({ label }: { label: string }) => (
  <Box sx={discoveryStatusRowSx}>
    <Typography sx={discoverySearchingLabelSx}>{label}</Typography>
    <CircularProgress
      size={16}
      sx={{ color: periverseTheme.palette.primary.dark }}
    />
  </Box>
);

const SuccessView = ({ title }: { title: string }) => (
  <Box sx={discoverySuccessOuterSx}>
    <Box sx={discoverySuccessPanelSx}>
      <Box component="span" sx={discoverySuccessIconWrapSx}>
        <CheckRoundedIcon
          sx={{ fontSize: '28px', color: periverseTheme.palette.success.dark }}
        />
      </Box>
      <Typography sx={discoverySuccessTitleSx}>{title}</Typography>
    </Box>
  </Box>
);

const WaitingView = ({
  waitingTitle,
  searchingLabel,
}: {
  waitingTitle: string;
  searchingLabel: string;
}) => (
  <Box sx={discoveryPanelSx}>
    <Box
      component="img"
      src={cartIllustration}
      alt="Cart illustration"
      sx={discoveryCartIllustrationSx}
    />
    <Box sx={discoveryWaitingCopySx}>
      <Typography sx={discoveryStatusTitleSx}>{waitingTitle}</Typography>
      <Box sx={discoveryStatusRowSx}>
        <Typography sx={discoveryStatusLabelSx}>{searchingLabel}</Typography>
        <CircularProgress size={16} />
      </Box>
    </Box>
  </Box>
);

const DetectedAssetRow = ({
  asset,
  onToggle,
}: {
  asset: DetectedAsset;
  onToggle: (assetId: string) => void;
}) => (
  <Box
    sx={discoveryDetectedTrayRowSx(asset.selected)}
    onClick={() => onToggle(asset.id)}
    role="button"
    tabIndex={0}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggle(asset.id);
      }
    }}
  >
    <Grid
      container
      direction="row"
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <Grid size={10}>
        <Stack
          direction="column"
          spacing={0}
          sx={{
            justifyContent: 'center',
            alignItems: 'flex-start',
            gap: '4px',
          }}
        >
          <Typography sx={discoveryDetectedTrayNameSx}>{asset.name}</Typography>
          <Box sx={discoveryDetectedTrayBadgeSx}>
            <Typography sx={discoveryDetectedTrayBadgeTextSx}>
              {asset.subtitle} Detected
            </Typography>
          </Box>
        </Stack>
      </Grid>
      <Grid size={2} display="flex" justifyContent="flex-end">
        <Checkbox
          checked={asset.selected}
          onClick={(event) => event.stopPropagation()}
          onChange={() => onToggle(asset.id)}
          inputProps={{ 'aria-label': `Select ${asset.name}` }}
          sx={discoveryCheckboxSx}
        />
      </Grid>
    </Grid>
  </Box>
);

const DetectedView = ({
  detectedAssets,
  detectedTitle,
  searchingLabel,
  onToggleAsset,
}: {
  detectedAssets: DetectedAsset[];
  detectedTitle: string;
  searchingLabel: string;
  onToggleAsset: (assetId: string) => void;
}) => (
  <Box sx={discoveryDetectedWrapSx}>
    <Typography sx={discoveryDetectedTitleSx}>{detectedTitle}</Typography>
    <Box sx={discoveryDetectedListSx}>
      {detectedAssets.map((asset) => (
        <DetectedAssetRow
          key={asset.id}
          asset={asset}
          onToggle={onToggleAsset}
        />
      ))}
    </Box>
    <SearchingIndicator label={searchingLabel} />
  </Box>
);

type DiscoveryBodyProps = {
  isSuccess: boolean;
  detectedAssets: DetectedAsset[];
  labels: DiscoveryCardCopy;
  onToggleAsset: (assetId: string) => void;
};

const DiscoveryBody = ({
  isSuccess,
  detectedAssets,
  labels,
  onToggleAsset,
}: DiscoveryBodyProps) => {
  if (isSuccess) {
    return <SuccessView title={labels.successTitle} />;
  }
  if (detectedAssets.length === 0) {
    return (
      <WaitingView
        waitingTitle={labels.waitingTitle}
        searchingLabel={labels.searchingLabel}
      />
    );
  }
  return (
    <DetectedView
      detectedAssets={detectedAssets}
      detectedTitle={labels.detectedTitle}
      searchingLabel={labels.searchingLabel}
      onToggleAsset={onToggleAsset}
    />
  );
};

type DiscoveryCardProps = {
  header: DiscoveryHeader;
  isSuccess: boolean;
  isLoading: boolean;
  detectedAssets: DetectedAsset[];
  onCancel: () => void;
  onAssign: () => void;
  onToggleAsset: (assetId: string) => void;
  copy: DiscoveryCardCopy;
};

export const DiscoveryCard = ({
  header,
  isSuccess,
  isLoading,
  detectedAssets,
  onCancel,
  onAssign,
  onToggleAsset,
  copy,
}: DiscoveryCardProps) => {
  const labels = copy;
  const selectedCount = detectedAssets.filter((asset) => asset.selected).length;
  const hasNonCartSelected = detectedAssets.some(
    (asset) => asset.selected && asset.assetType !== AssetType.CASE_CART
  );

  return (
    <Box sx={discoveryContainerSx}>
      <Box sx={discoveryHeaderWrapSx}>
        <Box sx={discoveryHeaderSx}>
          <Box sx={discoveryHeaderTitleStackSx}>
            <Typography sx={discoveryTitleSx}>{header.title}</Typography>
            <Typography sx={discoverySubtitleSx}>{header.subtitle}</Typography>
          </Box>
          <Box sx={discoveryActionsSx}>
            <Button
              variant="outlined"
              size="large"
              disabled={isLoading}
              onClick={onCancel}
              sx={discoveryCancelButtonSx}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              size="large"
              disabled={
                isLoading ||
                isSuccess ||
                selectedCount === 0 ||
                !hasNonCartSelected
              }
              onClick={onAssign}
              sx={discoveryAssignButtonSx}
            >
              {isLoading ? (
                <CircularProgress
                  size={16}
                  sx={{ color: periverseTheme.palette.primary.dark }}
                />
              ) : (
                <>
                  {labels.assignLabel}
                  {selectedCount > 0 ? ` (${selectedCount})` : ''}
                </>
              )}
            </Button>
          </Box>
        </Box>
      </Box>
      <Box sx={discoveryCardWrapSx}>
        <Paper sx={discoveryCardSx}>
          <DiscoveryBody
            isSuccess={isSuccess}
            detectedAssets={detectedAssets}
            labels={labels}
            onToggleAsset={onToggleAsset}
          />
        </Paper>
      </Box>
    </Box>
  );
};
